import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api from "../../api/axios";

import purpleIcon from "../../assets/images/user-group-purple.png";
import redIcon from "../../assets/images/user-group-red.png";

import Popup from "../../components/Reusable/Popup";
import DocumentPopup from "../../components/Reusable/DocumentPopup";

import { useLeaveManagementStore } from "../../store/useLeaveManagementStore";
import useLeaveCountStore from "../../store/useLeaveCountStore";
import LeaveDetailsGrid from "./components/LeaveDetailsGrid";
import EmployeeLeaveHeader from "./components/EmployeeLeaveHeader";

const ApproveDeclineLeave = ({
  setShowApproveDeclinePage,
  employeeData,
  setSelectedEmployeeData,
}) => {
  const permissions = JSON.parse(localStorage.getItem("userPermissions"));
  const [showApprovePopup, setShowApprovePopup] = useState(false);
  const [showDeclinePopup, setShowDeclinePopup] = useState(false);
  const [showForwardPopup, setShowForwardPopup] = useState(false);
  const [showDocumentPopup, setShowDocumentPopup] = useState(false);

  const refreshLeaveData = useLeaveManagementStore(
    (state) => state.refreshLeaveData,
  );
  const leaveCount = useLeaveCountStore((state) => state.leaveCount);
  const fetchLeaveCount = useLeaveCountStore((state) => state.fetchLeaveCount);

  const loggedInName = JSON.parse(localStorage.getItem("userData"))?.user?.[0]
    ?.employee_full_name;

  const approvingManagerStatus = employeeData?.approving_managers_status?.find(
    (manager) => manager?.manager_name?.trim() === loggedInName?.trim(),
  )?.forwarded;

  const isPending =
    employeeData?.is_approved === null && employeeData?.is_hr_approved === null;

  const isApproved =
    employeeData?.is_approved === 1 || employeeData?.is_hr_approved === 1;

  const employeeInfoId = employeeData?.user?.employee_information_id;
  const empCountry = employeeData?.user?.roles[0]?.company[0]?.country;

  useEffect(() => {
    if (employeeInfoId) {
      fetchLeaveCount(employeeInfoId);
    }
  }, [employeeInfoId, fetchLeaveCount]);

  const handleApprove = () => {
    setShowApprovePopup(true);
  };

  const handleDecline = () => {
    setShowDeclinePopup(true);
  };

  const handleForward = () => {
    setShowForwardPopup(true);
  };

  const hasViewManagedLeaveHistoryPermission = permissions.some(
    (permission) => permission.name === "view_managed_leave_history",
  );
  const hasApproveLeavePermission = permissions.some(
    (permission) => permission.name === "approve_leave",
  );
  const hasViewAllLeaveHistoryPermission = permissions.some(
    (permission) => permission.name === "view_all_leave_history",
  );
  const hasApproveLeaveHRPermission = permissions.some(
    (permission) => permission.name === "approve_leave_hr",
  );

  const handleLeaveActionSuccess = async (closePopup) => {
    await refreshLeaveData();
    await fetchLeaveCount(employeeInfoId);

    closePopup(false);
    setShowApproveDeclinePage(null);
  };

  const getApprovalConfig = () => {
    if (hasViewManagedLeaveHistoryPermission && hasApproveLeavePermission) {
      return {
        apiUrl: "/employees/leaves/managerLeaveApproval",
        approvalType: "is_approved",
        forwardType: "forward",
        approvalField: "deny_reason",
      };
    }

    if (hasViewAllLeaveHistoryPermission && hasApproveLeaveHRPermission) {
      return {
        apiUrl: "/employees/leaves/HrLeaveApproval",
        approvalType: "is_hr_approved",
        forwardType: "forward",
        approvalField: "deny_reason",
      };
    }

    return null;
  };

  const handleLeaveAction = async ({
    leaveId,
    approvalValue,
    forwardValue,
    reason,
    closePopup,
  }) => {
    const config = getApprovalConfig();

    if (!config) {
      alert("User does not have required permissions.");
      return;
    }

    const { apiUrl, approvalType, approvalField, forwardType } = config;

    try {
      const response = await api.put(apiUrl, {
        leave_id: leaveId,
        [approvalType]: approvalValue,
        [forwardType]: forwardValue,
        [approvalField]: reason,
      });

      await handleLeaveActionSuccess(closePopup);

      if (response?.data?.success) {
        setSelectedEmployeeData(null);
      }
    } catch (error) {
      console.log("Error", error);
    }
  };

  const handleApproveYesButton = (leaveId) =>
    handleLeaveAction({
      leaveId,
      approvalValue: 1,
      forwardValue: 0,
      reason: "Leave Approved",
      closePopup: setShowApprovePopup,
    });

  const handleForwardYesButton = (leaveId) =>
    handleLeaveAction({
      leaveId,
      approvalValue: 1,
      forwardValue: 1,
      reason: "Because",
      closePopup: setShowForwardPopup,
    });

  const handleDeclineYesButton = (leaveId) =>
    handleLeaveAction({
      leaveId,
      approvalValue: 0,
      forwardValue: 0,
      reason: "Declined",
      closePopup: setShowDeclinePopup,
    });

  const handleApproveNoButton = () => {
    setShowApprovePopup(false);
  };

  const handleDeclineNoButton = () => {
    setShowDeclinePopup(false);
  };
  const handleForwardNoButton = () => {
    setShowForwardPopup(false);
  };

  const handleOpenDocumentPopup = () => {
    setShowDocumentPopup(true);
  };

  const employeeLeaveCounts = leaveCount.filter(
    (leave) => leave.country === empCountry,
  );

  const totalLeavesRaw = employeeLeaveCounts.reduce(
    (total, leave) => total + Number(leave.count || 0),
    0,
  );

  const totalLeaves = Math.round(totalLeavesRaw);

  const totalLeavesTakenRaw = employeeLeaveCounts.reduce(
    (total, leave) => total + Number(leave.leave_days_taken || 0),
    0,
  );

  const totalLeavesTaken = Math.round(totalLeavesTakenRaw);

  const getStatusBadge = () => {
    if (employeeData?.is_approved === 1 || employeeData?.is_hr_approved === 1) {
      return {
        label: "Approved",
        className: "bg-emerald-50 text-emerald-700 ring-emerald-200",
      };
    }
    if (employeeData?.is_approved === 0 || employeeData?.is_hr_approved === 0) {
      return {
        label: "Declined",
        className: "bg-red-50 text-red-700 ring-red-200",
      };
    }
    return {
      label: "Pending",
      className: "bg-amber-50 text-amber-700 ring-amber-200",
    };
  };
  const statusBadge = getStatusBadge();

  const managerLabel =
    employeeData?.is_hr_approved || employeeData?.is_approved
      ? "Approved Manager"
      : employeeData?.is_hr_approved === 0 || employeeData?.is_approved === 0
        ? "Rejected Manager"
        : "Authorized Manager";

  const managerValue =
    employeeData?.is_hr_approved || employeeData?.is_approved
      ? employeeData?.approving_manager
      : employeeData?.is_hr_approved === 0 || employeeData?.is_approved === 0
        ? employeeData?.rejecting_manager
        : "—";

  // console.log(
  //   "Ddfdf",
  //   hasViewManagedLeaveHistoryPermission,
  //   hasApproveLeavePermission,
  //   approvingManagerStatus === false,
  // );

  return (
    <div className="absolute top-0.5 right-20 w-[70%] h-[99%] m-auto flex flex-col rounded-2xl bg-white shadow-2xl ring-1 ring-black/5 overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-8 py-3 border-b border-gray-300 bg-white">
        <div>
          <h3 className="text-xl font-semibold text-charcoal tracking-tight">
            Approve / Decline Leave Request
          </h3>
          <p className="text-sm text-muted mt-0.5">
            Review the details below before taking action
          </p>
        </div>
        <button
          type="button"
          onClick={() => setShowApproveDeclinePage(null)}
          className="flex items-center justify-center h-8 w-8 rounded-full text-desc-gray hover:bg-gray-100 hover:text-gray-700 transition-colors"
          aria-label="Close"
        >
          <span className="text-lg leading-none">&times;</span>
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto px-8 py-6 space-y-6">
        {/* Profile + leave stats */}
        <EmployeeLeaveHeader
          employeeData={employeeData}
          statusBadge={statusBadge}
          totalLeaves={totalLeaves}
          totalLeavesTaken={totalLeavesTaken}
          employeeLeaveCounts={employeeLeaveCounts}
          purpleIcon={purpleIcon}
          redIcon={redIcon}
        />

        {/* Employee ID / Applied date / Leave history */}
        <div className="flex items-center justify-between rounded-xl bg-gray-50 px-5 py-3">
          <div className="flex items-center gap-5 text-sm">
            <div>
              <span className="text-gray-400">Employee ID</span>
              <span className="ml-1.5 font-semibold text-gray-700">
                {employeeData?.user?.employee_id}
              </span>
            </div>
            <div className="h-4 w-px bg-gray-200" />
            <div>
              <span className="text-gray-400">Applied on</span>
              <span className="ml-1.5 font-semibold text-gray-700">
                {employeeData?.apply_date}
              </span>
            </div>
          </div>
          {employeeInfoId && (
            <Link
              to={`/employeeleavehistory/${employeeInfoId}`}
              className="inline-flex items-center gap-1 text-sm font-medium text-[var(--secondary-color)] hover:underline"
            >
              View Leave History
              <span aria-hidden="true">&rsaquo;</span>
            </Link>
          )}
        </div>

        {/* Leave details grid */}
        <LeaveDetailsGrid
          employeeData={employeeData}
          managerLabel={managerLabel}
          managerValue={managerValue}
        />

        {/* Reason */}
        <div className="rounded-xl border border-gray-100 bg-gray-50 px-5 py-2">
          <p className="text-sm font-medium  tracking-wide text-gray-400">
            Reason for leave:{" "}
            <span className=" text-xs mt-1.5 capitalize text-gray-700 leading-relaxed">
              {employeeData?.purpose}
            </span>
          </p>
        </div>

        <button
          type="button"
          onClick={handleOpenDocumentPopup}
          className="inline-flex items-center gap-1 text-sm font-semibold text-[#e13232] hover:underline"
        >
          View document
          <span aria-hidden="true">&rsaquo;</span>
        </button>

        {/* Remarks */}
        <div>
          <label
            htmlFor="remarks"
            className="block text-sm font-medium text-gray-600 mb-1.5"
          >
            Remarks / Notes
          </label>
          <textarea
            name="remarks"
            id="remarks"
            rows={3}
            placeholder="Add a note (optional)"
            className="w-full rounded-xl border border-gray-200 px-3.5 py-2.5 text-sm text-gray-700 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[var(--secondary-color)]/20 focus:border-[var(--secondary-color)] transition"
          ></textarea>
        </div>
      </div>

      {/* Actions */}
      <div className="flex items-center justify-end gap-3 px-8 py-3 border-t border-gray-100 bg-white">
        {isPending && (
          <button
            type="button"
            className="rounded-full px-6 h-10 text-sm font-semibold !bg-primary-hrms text-white shadow-sm hover:opacity-90 active:scale-[0.98] transition disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleApprove}
            disabled={isApproved}
          >
            Approve
          </button>
        )}

        {hasViewManagedLeaveHistoryPermission &&
          hasApproveLeavePermission &&
          approvingManagerStatus === false && (
            <button
              type="button"
              className="rounded-full px-6 h-10 border border-orange-500 bg-orange-500 text-white text-sm font-semibold hover:bg-orange-600 active:scale-[0.98] transition"
              onClick={handleForward}
            >
              Approve & Forward
            </button>
          )}

        {(isApproved || isPending) && (
          <button
            type="button"
            className="rounded-full px-6 h-10 border border-red-200 bg-red-600 text-white text-sm font-semibold hover:bg-red-400 active:scale-[0.98] transition"
            onClick={handleDecline}
          >
            Decline
          </button>
        )}
      </div>

      {showApprovePopup && (
        <Popup
          title="Approve Leave Request"
          message="Are you sure that you are approving this request?"
          onConfirm={handleApproveYesButton}
          onCancel={handleApproveNoButton}
          leaveId={employeeData?.leave_id}
        />
      )}

      {showDeclinePopup && (
        <Popup
          title="Decline Leave Request"
          message="Are you sure that you are declining this request?"
          onConfirm={handleDeclineYesButton}
          onCancel={handleDeclineNoButton}
          leaveId={employeeData?.leave_id}
        />
      )}
      {showForwardPopup && (
        <Popup
          title="Approve & Forward Leave Request"
          message="Are you sure that you are approving & forwarding this request?"
          onConfirm={handleForwardYesButton}
          onCancel={handleForwardNoButton}
          leaveId={employeeData?.leave_id}
        />
      )}
      {showDocumentPopup && (
        <DocumentPopup
          documentUrl={employeeData.document_url}
          onClose={() => setShowDocumentPopup(false)}
        />
      )}
    </div>
  );
};

export default ApproveDeclineLeave;
